import { useEffect, useMemo, useRef, useState } from 'react';
import { motion as Motion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

/* ─────────────────────────────────────────────────────
   CURSOR-REACTIVE PARTICLE FIELD
───────────────────────────────────────────────────── */
const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: 4 + ((i * 13) % 92),
  y: 6 + ((i * 19) % 86),
  size: 2 + (i % 4) * 1.2,
  speed: 12 + (i % 5) * 3.4,
  delay: i * 0.22,
  driftX: ((i % 2 === 0 ? 1 : -1) * (8 + (i % 4) * 6)),
  driftY: -(6 + (i % 5) * 5),
  opacity: 0.1 + (i % 4) * 0.05,
}));

function ParticleField({ reducedMotion }) {
  return (
    <div className="vision-particles" aria-hidden="true">
      {PARTICLES.map((p) => (
        <Motion.span
          key={p.id}
          className="vision-particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={
            reducedMotion
              ? undefined
              : {
                  x: [0, p.driftX, p.driftX * 0.3, 0],
                  y: [0, p.driftY, p.driftY * 0.6, 0],
                  opacity: [p.opacity * 0.6, p.opacity, p.opacity * 0.8, p.opacity * 0.6],
                  scale: [0.8, 1.1, 0.95, 0.8],
                }
          }
          transition={
            reducedMotion
              ? undefined
              : {
                  duration: p.speed,
                  delay: p.delay,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
          }
        />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   HOLOGRAPHIC HEADLINE — letter-by-letter reveal
───────────────────────────────────────────────────── */
function HoloHeadline({ text, reducedMotion }) {
  const chars = useMemo(() => [...text], [text]);

  if (reducedMotion) {
    return <h2 className="vision-holo-headline">{text}</h2>;
  }

  return (
    <h2 className="vision-holo-headline" aria-label={text}>
      {chars.map((char, i) => (
        <Motion.span
          key={i}
          className="vision-holo-headline__char"
          initial={{ opacity: 0, y: 28, filter: 'blur(14px)', rotateX: -60 }}
          whileInView={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            rotateX: 0,
            transition: {
              duration: 0.55,
              delay: i * 0.034,
              ease: EASE,
            },
          }}
          viewport={{ once: true, amount: 0.5 }}
        >
          {char === ' ' ? '\u00a0' : char}
        </Motion.span>
      ))}
    </h2>
  );
}

/* ─────────────────────────────────────────────────────
   ROTATING GLASS CUBE with illuminated keywords
───────────────────────────────────────────────────── */
const CUBE_KEYWORDS = ['Intelligence', 'Architecture', 'Velocity', 'Craft', 'Vision', 'Precision'];

function GlassCube({ reducedMotion }) {
  return (
    <div className="vision-cube-wrap" aria-hidden="true">
      <div className="vision-cube-glow" />
      <Motion.div
        className="vision-cube"
        animate={
          reducedMotion
            ? undefined
            : {
                rotateY: [0, 360],
                rotateX: [8, 22, 8],
              }
        }
        transition={
          reducedMotion
            ? undefined
            : {
                rotateY: { duration: 24, ease: 'linear', repeat: Infinity },
                rotateX: { duration: 9, ease: 'easeInOut', repeat: Infinity },
              }
        }
      >
        {/* 6 faces */}
        {['front', 'back', 'left', 'right', 'top', 'bottom'].map((face, i) => (
          <div key={face} className={`vision-cube__face vision-cube__face--${face}`}>
            <span className="vision-cube__keyword">{CUBE_KEYWORDS[i]}</span>
          </div>
        ))}
      </Motion.div>

      {/* Orbit ring */}
      <Motion.div
        className="vision-cube__orbit-ring"
        animate={reducedMotion ? undefined : { rotateZ: [0, 360] }}
        transition={reducedMotion ? undefined : { duration: 18, ease: 'linear', repeat: Infinity }}
      />
      <Motion.div
        className="vision-cube__orbit-ring vision-cube__orbit-ring--2"
        animate={reducedMotion ? undefined : { rotateZ: [0, -360] }}
        transition={reducedMotion ? undefined : { duration: 26, ease: 'linear', repeat: Infinity }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PHILOSOPHY GLASS CARDS with animated diagram lines
───────────────────────────────────────────────────── */
const CARD_ROTATIONS = [-1.2, 0.8, -0.6, 1.4, -1, 0.5];

function PhilosophyCard({ pillar, index, reducedMotion }) {
  const [hovered, setHovered] = useState(false);
  const rot = CARD_ROTATIONS[index % CARD_ROTATIONS.length];

  return (
    <Motion.article
      className="vision-philosophy-card glass-card"
      initial={reducedMotion ? undefined : { opacity: 0, y: 40, rotate: rot * 1.5, filter: 'blur(8px)' }}
      whileInView={
        reducedMotion
          ? undefined
          : {
              opacity: 1,
              y: 0,
              rotate: rot,
              filter: 'blur(0px)',
              transition: { duration: 0.7, delay: index * 0.08, ease: EASE },
            }
      }
      whileHover={
        reducedMotion
          ? undefined
          : {
              y: -8,
              rotate: rot * 0.3,
              scale: 1.02,
              transition: { duration: 0.36, ease: EASE },
            }
      }
      viewport={{ once: true, amount: 0.2 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {/* Glow edge pulse on hover */}
      <Motion.div
        className="vision-philosophy-card__glow-edge"
        animate={hovered && !reducedMotion ? { opacity: [0.4, 0.9, 0.4] } : { opacity: 0 }}
        transition={{ duration: 1.4, ease: 'easeInOut', repeat: hovered ? Infinity : 0 }}
      />

      {/* Animated diagram line */}
      <svg className="vision-philosophy-card__diagram" viewBox="0 0 120 32" aria-hidden="true">
        <Motion.line
          x1="8" y1="16" x2="112" y2="16"
          stroke="rgba(0,229,255,0.25)"
          strokeWidth="1"
          initial={reducedMotion ? undefined : { pathLength: 0 }}
          whileInView={
            reducedMotion
              ? undefined
              : { pathLength: 1, transition: { duration: 0.9, delay: index * 0.08 + 0.3, ease: EASE } }
          }
          viewport={{ once: true }}
        />
        <Motion.circle
          cx="8" cy="16" r="3"
          fill="rgba(0,229,255,0.4)"
          initial={reducedMotion ? undefined : { scale: 0, opacity: 0 }}
          whileInView={
            reducedMotion
              ? undefined
              : { scale: 1, opacity: 1, transition: { duration: 0.4, delay: index * 0.08 + 0.5 } }
          }
          viewport={{ once: true }}
        />
        <Motion.circle
          cx="112" cy="16" r="3"
          fill="rgba(151,246,255,0.6)"
          initial={reducedMotion ? undefined : { scale: 0, opacity: 0 }}
          whileInView={
            reducedMotion
              ? undefined
              : { scale: 1, opacity: 1, transition: { duration: 0.4, delay: index * 0.08 + 0.9 } }
          }
          viewport={{ once: true }}
        />
      </svg>

      <div className="vision-philosophy-card__index">
        {String(index + 1).padStart(2, '0')}
      </div>
      <h3>{pillar.title}</h3>
      <p>{pillar.description}</p>
    </Motion.article>
  );
}

/* ─────────────────────────────────────────────────────
   FUTURE TIMELINE SLIDER
───────────────────────────────────────────────────── */
const TIMELINE_NODES = [
  { year: '2020', label: 'Origins', detail: 'First production apps shipped solo. The architecture obsession begins.' },
  { year: '2022', label: 'Depth', detail: 'SaaS systems, AR prototypes, cloud pipelines — depth over breadth.' },
  { year: '2024', label: 'Studio', detail: 'Sanjivani DevWorks founded. Four flagships shipped in Year One.' },
  { year: '2025', label: 'Scale', detail: 'Complex multi-tenant platforms, ML dashboards, immersive 3D web.' },
  { year: '2026', label: 'Vision', detail: 'Expanding into AI-integrated products and design system consultancy.' },
  { year: '2028', label: 'Future', detail: 'Spatial computing interfaces. The next layer of digital product craft.' },
];

function FutureTimeline({ reducedMotion }) {
  const [active, setActive] = useState(2);
  const trackRef = useRef(null);

  return (
    <div className="vision-timeline">
      <div className="vision-timeline__header">
        <span className="vision-timeline__eyebrow">Technological Evolution</span>
      </div>

      {/* Energy wave line */}
      <div className="vision-timeline__rail" ref={trackRef}>
        <Motion.div
          className="vision-timeline__rail-fill"
          animate={reducedMotion ? undefined : { scaleX: (active + 1) / TIMELINE_NODES.length }}
          initial={reducedMotion ? undefined : { scaleX: 0 }}
          transition={reducedMotion ? undefined : { duration: 0.9, ease: EASE }}
          style={{ transformOrigin: 'left center' }}
        />

        {/* Energy wave pulse */}
        {!reducedMotion && (
          <Motion.div
            className="vision-timeline__wave"
            animate={{ x: ['0%', '100%'] }}
            transition={{ duration: 2.4, ease: 'easeInOut', repeat: Infinity, repeatType: 'loop' }}
          />
        )}

        {TIMELINE_NODES.map((node, i) => (
          <button
            key={node.year}
            type="button"
            className={`vision-timeline__node ${i === active ? 'is-active' : ''} ${i < active ? 'is-past' : ''}`}
            style={{ left: `${(i / (TIMELINE_NODES.length - 1)) * 100}%` }}
            onClick={() => setActive(i)}
            aria-label={`${node.year} — ${node.label}`}
          >
            <Motion.div
              className="vision-timeline__node-ring"
              animate={
                i === active && !reducedMotion
                  ? { scale: [1, 1.65, 1], opacity: [0.6, 0, 0.6] }
                  : { scale: 1, opacity: 0 }
              }
              transition={{ duration: 2.2, ease: 'easeOut', repeat: i === active ? Infinity : 0 }}
            />
            <div className="vision-timeline__node-dot" />
            <span className="vision-timeline__node-year">{node.year}</span>
          </button>
        ))}
      </div>

      {/* Detail panel */}
      <Motion.div
        key={active}
        className="vision-timeline__detail glass-card"
        initial={reducedMotion ? undefined : { opacity: 0, y: 18, filter: 'blur(8px)' }}
        animate={reducedMotion ? undefined : { opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={reducedMotion ? undefined : { duration: 0.5, ease: EASE }}
      >
        <span className="vision-timeline__detail-year">{TIMELINE_NODES[active].year}</span>
        <strong className="vision-timeline__detail-label">{TIMELINE_NODES[active].label}</strong>
        <p className="vision-timeline__detail-text">{TIMELINE_NODES[active].detail}</p>
      </Motion.div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   NEURAL NETWORK SVG BACKDROP LINES
───────────────────────────────────────────────────── */
function NeuralLines({ reducedMotion }) {
  return (
    <svg className="vision-neural-lines" viewBox="0 0 1200 600" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
      {/* Background connector lines */}
      {[
        [120, 80, 480, 200],
        [480, 200, 820, 110],
        [820, 110, 1100, 260],
        [280, 420, 480, 200],
        [480, 200, 700, 380],
        [700, 380, 1050, 440],
        [120, 80, 280, 420],
        [820, 110, 700, 380],
        [1100, 260, 1050, 440],
      ].map(([x1, y1, x2, y2], i) => (
        <Motion.line
          key={i}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="rgba(0,229,255,0.07)"
          strokeWidth="1"
          initial={reducedMotion ? undefined : { pathLength: 0, opacity: 0 }}
          animate={reducedMotion ? undefined : { pathLength: 1, opacity: 1 }}
          transition={reducedMotion ? undefined : { duration: 1.8, delay: i * 0.18, ease: EASE }}
        />
      ))}
      {/* Travelling energy pulse on main path */}
      {!reducedMotion && (
        <Motion.circle
          r="3"
          fill="rgba(0,229,255,0.5)"
          filter="url(#glow)"
          initial={{ cx: 120, cy: 80 }}
          animate={{
            cx: [120, 480, 820, 1100],
            cy: [80, 200, 110, 260],
          }}
          transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity, repeatType: 'reverse' }}
        />
      )}
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Nodes */}
      {[[120, 80], [480, 200], [820, 110], [1100, 260], [280, 420], [700, 380], [1050, 440]].map(([cx, cy], i) => (
        <Motion.circle
          key={i}
          cx={cx} cy={cy} r="5"
          fill="rgba(0,229,255,0.22)"
          stroke="rgba(0,229,255,0.45)"
          strokeWidth="1"
          animate={reducedMotion ? undefined : { opacity: [0.5, 1, 0.5] }}
          transition={reducedMotion ? undefined : { duration: 3.2 + i * 0.4, delay: i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </svg>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN VISION SECTION
───────────────────────────────────────────────────── */
export default function VisionSection({ content, reducedMotion = false }) {
  const { philosophy } = content;

  const sectionRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    if (reducedMotion) return;
    const el = sectionRef.current;
    if (!el) return;

    function onMove(e) {
      const rect = el.getBoundingClientRect();
      setMousePos({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    }

    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, [reducedMotion]);

  const parallaxX = (mousePos.x - 0.5) * 18;
  const parallaxY = (mousePos.y - 0.5) * 10;

  return (
    <section id="vision" className="story-section vision-section" ref={sectionRef}>
      {/* Deep space volumetric backdrop */}
      <div className="vision-backdrop" aria-hidden="true">
        <div className="vision-backdrop__fog vision-backdrop__fog--1" />
        <div className="vision-backdrop__fog vision-backdrop__fog--2" />
        <div className="vision-backdrop__fog vision-backdrop__fog--3" />
        <NeuralLines reducedMotion={reducedMotion} />
        <ParticleField reducedMotion={reducedMotion} />
      </div>

      {/* Scanline overlay */}
      <div className="vision-scanlines" aria-hidden="true" />

      <div className="section-shell vision-shell">

        {/* ── Holographic transmission headline ── */}
        <div className="vision-hero">
          <Motion.div
            className="vision-hero__eyebrow"
            initial={reducedMotion ? undefined : { opacity: 0, letterSpacing: '0.6em' }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, letterSpacing: '0.18em', transition: { duration: 1, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.5 }}
          >
            <span className="vision-hero__signal" aria-hidden="true" />
            {philosophy.eyebrow}
          </Motion.div>

          <HoloHeadline text={philosophy.statement} reducedMotion={reducedMotion} />

          <Motion.p
            className="vision-hero__description"
            initial={reducedMotion ? undefined : { opacity: 0, y: 20, filter: 'blur(8px)' }}
            whileInView={
              reducedMotion
                ? undefined
                : { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.8, delay: 0.3, ease: EASE } }
            }
            viewport={{ once: true, amount: 0.4 }}
          >
            {philosophy.description}
          </Motion.p>
        </div>

        {/* ── Central rotating glass cube + philosophy fabric ── */}
        <div className="vision-center-stage">
          <Motion.div
            className="vision-cube-section"
            style={
              reducedMotion
                ? undefined
                : { transform: `translate(${parallaxX * 0.4}px, ${parallaxY * 0.4}px)` }
            }
          >
            <GlassCube reducedMotion={reducedMotion} />
          </Motion.div>

          {/* Keywords cloud around the cube */}
          <div className="vision-keywords">
            {philosophy.keywords.map((kw, i) => (
              <Motion.span
                key={kw}
                className="vision-keyword"
                initial={reducedMotion ? undefined : { opacity: 0, scale: 0.7 }}
                whileInView={
                  reducedMotion
                    ? undefined
                    : { opacity: 1, scale: 1, transition: { duration: 0.5, delay: i * 0.1, ease: EASE } }
                }
                whileHover={reducedMotion ? undefined : { scale: 1.12, y: -4, transition: { duration: 0.25 } }}
                viewport={{ once: true }}
              >
                {kw}
              </Motion.span>
            ))}
          </div>
        </div>

        {/* ── Philosophy glass cards ── */}
        <div className="vision-philosophy-grid">
          {philosophy.pillars.map((pillar, i) => (
            <PhilosophyCard
              key={pillar.title}
              pillar={pillar}
              index={i}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
